import clsx from 'clsx';
import { type FC, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { getMeetingUrl } from '../../../../utils/environment';
import { GENERATE_UNIQUE_MEETING_ID, MEETING_ID_SELECTION_TITLE } from '../../helpers/constants';
import type { MeetingIdOption, MeetingIdSelectProps } from '../../types';

const MeetingIdSelect: FC<MeetingIdSelectProps> = ({
  options,
  selectedValue,
  isLoading,
  isAllocating,
  onChange,
}) => {
  const id = useId();
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Capture phase: the modal stops `mousedown` from propagating past its own
    // root, so a bubbling listener would never see clicks made inside the modal.
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    return () => document.removeEventListener('mousedown', handlePointerDown, true);
  }, [isOpen]);

  const selectedOption =
    options.find((option) => option.value === selectedValue) ??
    options.find((option) => option.value === GENERATE_UNIQUE_MEETING_ID) ??
    options[0];

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape' && isOpen) {
      // Otherwise the modal's document-level Escape handler closes the whole modal.
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  const handleSelect = (value: string) => {
    setIsOpen(false);
    onChange(value);
  };

  /**
   * List rows carry the join URL and mark "Generate" as an add action; the
   * closed trigger shows only the chosen label.
   */
  const renderOptionBody = (
    option: MeetingIdOption,
    { inList, id }: { inList: boolean; id?: string },
  ) => (
    <span className="schedule-meeting-modal__meeting-id-option" id={id}>
      <span
        className={clsx('schedule-meeting-modal__meeting-id-option-label', {
          'schedule-meeting-modal__meeting-id-option-label--generate': option.isGenerate && inList,
        })}
      >
        {option.isGenerate && inList ? `+ ${option.label}` : option.label}
      </span>
      {inList && option.meetingId && (
        <span className="schedule-meeting-modal__meeting-id-option-id">
          {getMeetingUrl(option.meetingId)}
        </span>
      )}
    </span>
  );

  return (
    <>
      <div className="schedule-meeting-modal__divider" />
      <div className="schedule-meeting-modal__meeting-id-section">
        <div className="schedule-meeting-modal__input-label" id={labelId}>
          {MEETING_ID_SELECTION_TITLE}
        </div>
        <div className="schedule-meeting-modal__meeting-id-select" ref={containerRef}>
          <button
            type="button"
            className="schedule-meeting-modal__meeting-id-trigger"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby={`${labelId} ${valueId}`}
            disabled={isLoading || isAllocating}
            onClick={() => setIsOpen((wasOpen) => !wasOpen)}
            onKeyDown={handleKeyDown}
          >
            {renderOptionBody(selectedOption, { inList: false, id: valueId })}
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="schedule-meeting-modal__meeting-id-chevron"
              aria-hidden="true"
            >
              <path d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z" />
            </svg>
          </button>

          {isOpen && (
            <div
              className="schedule-meeting-modal__dropdown-menu schedule-meeting-modal__meeting-id-menu"
              role="listbox"
              aria-labelledby={labelId}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === selectedValue}
                  className={clsx(
                    'schedule-meeting-modal__dropdown-item',
                    'schedule-meeting-modal__meeting-id-item',
                    {
                      'schedule-meeting-modal__meeting-id-item--selected':
                        option.value === selectedValue,
                      'schedule-meeting-modal__meeting-id-item--generate': option.isGenerate,
                    },
                  )}
                  onClick={() => handleSelect(option.value)}
                  onKeyDown={handleKeyDown}
                >
                  {renderOptionBody(option, { inList: true })}
                </button>
              ))}
            </div>
          )}
        </div>
        {isAllocating && (
          <div className="schedule-meeting-modal__input-hint">Allocating a new meeting ID...</div>
        )}
      </div>
    </>
  );
};

export default MeetingIdSelect;
